import React, { useState, useEffect, useMemo } from "react";
import { Form, Row, Col, Alert, Space, Select, Image, Tooltip, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import ArrowDownIcon from "../../../assets/icons/arrow-down-icon.svg";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomInput from "../../../components/CustomInput/Input";
import CustomInputNumber from "../../../components/CustomInputNumber/InputNumber";
import CustomButton from "../../../components/CustomButton/Button";
import {
  companyNameRules,
  CommentsRules,
} from "../../../helpers/helpers";

const RequestCreditLimitModal = ({
  initialData = null,
  availableEnterprises = [],
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const [addEnterpriseForm] = Form.useForm();
  const isEditMode = Boolean(initialData?.requestId);

  const [customEnterprises, setCustomEnterprises] = useState([]);
  const [selectedEnterpriseMeta, setSelectedEnterpriseMeta] = useState(null);
  const [isAddEnterpriseModalOpen, setIsAddEnterpriseModalOpen] = useState(false);

  const allEnterpriseOptions = useMemo(() => {
    const existing = availableEnterprises.map((ent) => ({
      label: ent.entName,
      value: ent.entId,
      entId: ent.entId,
      entName: ent.entName,
      isNew: false,
    }));

    return [...customEnterprises, ...existing];
  }, [availableEnterprises, customEnterprises]);

  useEffect(() => {
    if (initialData) {
      if (initialData.entId) {
        setSelectedEnterpriseMeta({
          entId: initialData.entId,
          enterpriseName: initialData.enterpriseName,
          isNew: false,
        });
        form.setFieldsValue({
          selectedEnterprise: initialData.entId,
          requestedCredit: initialData.requestedCredit
            ? Number(initialData.requestedCredit)
            : undefined,
          remarks: initialData.remarks || "",
        });
      } else if (initialData.enterpriseName) {
        const newValue = `NEW_${initialData.enterpriseName}`;
        const initialNewOpt = {
          label: `${initialData.enterpriseName} (New Enterprise)`,
          value: newValue,
          entId: null,
          entName: initialData.enterpriseName,
          isNew: true,
        };
        setCustomEnterprises((prev) => {
          if (prev.some((item) => item.value === newValue)) return prev;
          return [initialNewOpt, ...prev];
        });
        setSelectedEnterpriseMeta({
          entId: null,
          enterpriseName: initialData.enterpriseName,
          isNew: true,
        });
        form.setFieldsValue({
          selectedEnterprise: newValue,
          requestedCredit: initialData.requestedCredit
            ? Number(initialData.requestedCredit)
            : undefined,
          remarks: initialData.remarks || "",
        });
      }
    } else {
      form.resetFields();
      setSelectedEnterpriseMeta(null);
    }
  }, [initialData, form]);

  const handleSelectChange = (value) => {
    const selected =
      allEnterpriseOptions.find((opt) => opt.value === value) ||
      customEnterprises.find((opt) => opt.value === value) ||
      availableEnterprises.find((opt) => opt.entId === value);

    if (selected) {
      const isNew = selected.isNew || value?.toString().startsWith("NEW_") || false;
      const entId = isNew ? null : selected.entId || selected.value;
      const entName = selected.entName || selected.label;

      setSelectedEnterpriseMeta({
        entId,
        enterpriseName: entName,
        isNew,
      });

      form.setFieldsValue({
        selectedEnterprise: value,
      });
    }
  };

  const handleAddEnterpriseSubmit = (values) => {
    const trimmedName = values.newEnterpriseName?.trim();
    if (!trimmedName) return;

    const existing = availableEnterprises.find(
      (e) => e.entName?.trim().toLowerCase() === trimmedName.toLowerCase()
    );

    if (existing) {
      handleSelectChange(existing.entId);
      setIsAddEnterpriseModalOpen(false);
      addEnterpriseForm.resetFields();
      message.info(`"${existing.entName}" already exists and has been selected.`);
      return;
    }

    const newValue = `NEW_${trimmedName}`;
    const newOpt = {
      label: `${trimmedName} (New Enterprise)`,
      value: newValue,
      entId: null,
      entName: trimmedName,
      isNew: true,
    };

    setCustomEnterprises((prev) => {
      const filtered = prev.filter(
        (item) => item.entName.toLowerCase() !== trimmedName.toLowerCase()
      );
      return [newOpt, ...filtered];
    });

    setSelectedEnterpriseMeta({
      entId: null,
      enterpriseName: trimmedName,
      isNew: true,
    });

    form.setFieldsValue({
      selectedEnterprise: newValue,
    });

    setIsAddEnterpriseModalOpen(false);
    addEnterpriseForm.resetFields();
    message.success(`Enterprise "${trimmedName}" added and selected.`);
  };

  const handleFinish = async (values) => {
    const selected = allEnterpriseOptions.find(
      (opt) => opt.value === values.selectedEnterprise
    );

    const isNew = selected
      ? selected.isNew
      : Boolean(
          selectedEnterpriseMeta?.isNew ||
            values.selectedEnterprise?.toString().startsWith("NEW_")
        );
    const resolvedEntId = selected
      ? selected.entId
      : selectedEnterpriseMeta?.entId;
    const resolvedEntName = selected
      ? selected.entName
      : selectedEnterpriseMeta?.enterpriseName;

    const payload = {
      enterpriseType: isNew ? "NEW" : "EXISTING",
      entId: isNew ? null : resolvedEntId || null,
      enterpriseName: resolvedEntName?.trim(),
      requestedCredit: values.requestedCredit,
      remarks: values.remarks || null,
    };

    await onSubmit(payload, initialData?.requestId);
  };

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
      >
        {initialData?.rejectionReason && (
          <Alert
            message="Previous Rejection Reason"
            description={initialData.rejectionReason}
            type="error"
            showIcon
            style={{ marginBottom: 20 }}
          />
        )}

        <Row gutter={[16, 0]}>
          <Col span={24}>
            <Form.Item
              label={
                <span>
                  Select Enterprise
                  <span style={{ color: "red", marginLeft: 4 }}>*</span>
                </span>
              }
              className="custom-select"
              style={{ marginBottom: 16 }}
            >
              <Row gutter={8} align="middle">
                <Col flex="auto">
                  <Form.Item
                    name="selectedEnterprise"
                    rules={[{ required: true, message: "Please select an enterprise" }]}
                    noStyle
                  >
                    <Select
                      placeholder="Search and select enterprise"
                      showSearch
                      suffixIcon={<Image src={ArrowDownIcon} alt="Arrow Down" preview={false} />}
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.children || "")
                          .toString()
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onChange={handleSelectChange}
                      style={{ width: "100%", height: 40 }}
                    >
                      {allEnterpriseOptions.map((option) => (
                        <Select.Option key={option.value} value={option.value}>
                          {option.isNew ? `${option.entName} (New Enterprise)` : option.entName}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col flex="40px">
                  <Tooltip title="Add New Enterprise">
                    <CustomButton
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setIsAddEnterpriseModalOpen(true)}
                      style={{
                        height: 40,
                        width: 40,
                        minWidth: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                      }}
                      data-testid="add-enterprise-btn"
                    />
                  </Tooltip>
                </Col>
              </Row>
            </Form.Item>
          </Col>

          <Col span={24}>
            <CustomInputNumber
              label="Requested Credit Limit (₹)"
              name="requestedCredit"
              isRequired={true}
              placeholder="e.g. 5,00,000"
              rules={[
                { required: true, message: "Please enter requested credit limit" },
                {
                  validator: (_, value) => {
                    if (value && Number(value) <= 0) {
                      return Promise.reject(new Error("Credit limit must be greater than 0"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Col>

          <Col span={24}>
            <CustomInput
              label="Remarks / Note"
              name="remarks"
              type="textarea"
              rows={3}
              placeholder="Add any additional details or notes for admin"
              rules={CommentsRules}
            />
          </Col>
        </Row>

        <Space style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
          <CustomButton onClick={onCancel} disabled={loading}>
            Cancel
          </CustomButton>
          <CustomButton type="primary" htmlType="submit" loading={loading} disabled={loading}>
            {isEditMode ? "Update & Resubmit Request" : "Submit Request"}
          </CustomButton>
        </Space>
      </Form>

      {isAddEnterpriseModalOpen && (
        <CustomModal
          title="Add New Enterprise"
          open={isAddEnterpriseModalOpen}
          onCancel={() => {
            setIsAddEnterpriseModalOpen(false);
            addEnterpriseForm.resetFields();
          }}
          centered
          width={450}
          content={
            <Form
              form={addEnterpriseForm}
              layout="vertical"
              onFinish={handleAddEnterpriseSubmit}
            >
              <CustomInput
                label="Enterprise Name"
                name="newEnterpriseName"
                isRequired={true}
                placeholder="Enter Enterprise Name"
                rules={companyNameRules("Enterprise")}
              />
              <Space
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 24,
                }}
              >
                <CustomButton
                  onClick={() => {
                    setIsAddEnterpriseModalOpen(false);
                    addEnterpriseForm.resetFields();
                  }}
                >
                  Cancel
                </CustomButton>
                <CustomButton type="primary" htmlType="submit">
                  Add Enterprise
                </CustomButton>
              </Space>
            </Form>
          }
        />
      )}
    </>
  );
};

export default RequestCreditLimitModal;
